import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Bell, Boxes, Building2, CalendarCheck, ChevronDown, ChevronRight,
  CircleDollarSign, ClipboardList, FileText, Gauge, Gavel, Globe2,
  GraduationCap, LayoutDashboard, LogOut, Menu, PackageCheck, PanelLeftClose,
  PanelLeftOpen, ReceiptText,
  Settings2, ShieldCheck, ShoppingCart, Store, UserRound, UsersRound, WalletCards, X
} from 'lucide-react';
import Footer from './Footer';
import { useSidebar } from '../context/SidebarContext';

const navigation = [
  { label: 'Overview', items: [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard, roles: ['Admin', 'SuperAdmin', 'Manager', 'HR Manager', 'Staff', 'Employee', 'Customer'] },
  ]},
  { label: 'Commerce', items: [
    { name: 'Product Management', path: '/products', icon: Boxes, roles: ['Admin', 'SuperAdmin', 'Manager', 'Staff'], children: [
      { name: 'Products & SKUs', path: '/products?tab=products' },
      { name: 'Brands', path: '/products?tab=brands' },
      { name: 'Variants', path: '/products?tab=variants' },
      { name: 'Pricing', path: '/pricing' },
    ]},
    { name: 'Orders', path: '/orders', icon: ShoppingCart, roles: ['Admin', 'SuperAdmin', 'Manager', 'Staff', 'Customer'] },
    { name: 'Franchisees', path: '/franchisee', icon: Store, roles: ['Admin', 'SuperAdmin', 'Manager'], children: [
      { name: 'Leads', path: '/franchisee?main=leads&sub=all-leads' },
      { name: 'Journey Pipeline', path: '/franchisee?main=journey&sub=timeline' },
      { name: 'Active Franchisees', path: '/franchisee?main=active&sub=franchisees' },
      { name: 'Performance', path: '/franchisee?main=active&sub=performance' },
      { name: 'Commissions', path: '/franchisee?main=active&sub=commissions' },
    ]},
    { name: 'Store Operations', path: '/store-operations', icon: PackageCheck, roles: ['Admin', 'SuperAdmin', 'Manager', 'Staff'] },
    { name: 'Store Setup', path: '/store-setup', icon: Settings2, roles: ['Admin', 'SuperAdmin', 'Manager'] },
  ]},
  { label: 'Procurement & Finance', items: [
    { name: 'Suppliers', path: '/supplier-management', icon: Building2, roles: ['Admin', 'SuperAdmin', 'Manager'] },
    { name: 'Purchases', path: '/purchase-management', icon: ClipboardList, roles: ['Admin', 'SuperAdmin', 'Manager'] },
    { name: 'Finance', path: '/finance-management', icon: CircleDollarSign, roles: ['Admin', 'SuperAdmin', 'Manager'] },
  ]},
  { label: 'People', items: [
    { name: 'HR Dashboard', path: '/hrms', icon: UsersRound, roles: ['Admin', 'SuperAdmin', 'Manager', 'HR Manager'] },
    { name: 'Employees', path: '/employees', icon: UserRound, roles: ['Admin', 'SuperAdmin', 'Manager', 'HR Manager'], children: [
      { name: 'Employee Directory', path: '/employees' },
      { name: 'Departments', path: '/departments' },
      { name: 'Onboarding', path: '/onboarding' },
    ]},
    { name: 'Attendance', path: '/attendance', icon: CalendarCheck, roles: ['Admin', 'SuperAdmin', 'Manager', 'HR Manager', 'Staff', 'Employee'], children: [
      { name: 'Attendance', path: '/attendance' },
      { name: 'Leaves', path: '/leaves' },
      { name: 'Shifts', path: '/shifts' },
      { name: 'Holidays', path: '/holidays' },
    ]},
    { name: 'Payroll', path: '/payroll', icon: WalletCards, roles: ['Admin', 'SuperAdmin', 'Manager', 'HR Manager'] },
    { name: 'Performance', path: '/performance', icon: GraduationCap, roles: ['Admin', 'SuperAdmin', 'Manager', 'HR Manager', 'Staff', 'Employee'] },
    { name: 'Documents', path: '/documents', icon: FileText, roles: ['Admin', 'SuperAdmin', 'Manager', 'HR Manager', 'Staff', 'Employee'], children: [
      { name: 'Employee Documents', path: '/documents' },
      { name: 'HR Policies', path: '/hr-policies' },
      { name: 'Assets', path: '/assets' },
      { name: 'Travel Expenses', path: '/travel-expense' },
      { name: 'Announcements', path: '/announcements' },
    ]},
  ]},
  { label: 'Website', items: [
    { name: 'Website CMS', path: '/cms', icon: Globe2, roles: ['Admin', 'SuperAdmin'] },
  ]},
  { label: 'Administration', items: [
    { name: 'Legal & Compliance', path: '/legal', icon: Gavel, roles: ['Admin', 'SuperAdmin', 'Manager'] },
    { name: 'Reports', path: '/reports', icon: ReceiptText, roles: ['Admin', 'SuperAdmin', 'Manager'] },
    { name: 'Roles & Access', path: '/rbac', icon: ShieldCheck, roles: ['Admin', 'SuperAdmin'] },
  ]},
];

const routeNames = navigation.flatMap((group) => group.items.flatMap((item) => [item, ...(item.children || [])]));
const legacyRouteNames = {
  '/product-management': { name: 'Product Management', icon: Boxes },
  '/pricing-discussion': { name: 'Pricing', icon: CircleDollarSign },
  '/hrms-dashboard': { name: 'HR Dashboard', icon: UsersRound },
  '/franchisee-management': { name: 'Franchisees', icon: Store },
  '/role-management': { name: 'Roles & Access', icon: ShieldCheck },
};

function Layout({ children, user, onLogout }) {
  const { sidebarOpen, toggleSidebar, expandedMenus, toggleMenu, expandMenu } = useSidebar();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const pathnameFor = (path) => path.split('?')[0];
  const canSee = (item) => !item.roles || item.roles.includes(user?.role);
  const visibleNavigation = useMemo(() => navigation
    .map((group) => ({ ...group, items: group.items.filter(canSee).map((item) => ({ ...item, children: item.children?.filter(canSee) })) }))
    .filter((group) => group.items.length), [user?.role]);
  const isActive = (path) => {
    const [target, query] = path.split('?');
    const pathnameMatches = target === '/' ? location.pathname === '/' : location.pathname === target || location.pathname.startsWith(`${target}/`);
    if (!pathnameMatches || !query) return pathnameMatches;
    const expected = new URLSearchParams(query);
    const current = new URLSearchParams(location.search);
    return [...expected.entries()].every(([key, value]) => current.get(key) === value);
  };

  const currentPage = useMemo(() => {
    const matches = routeNames.filter((item) => isActive(item.path));
    return matches.sort((a, b) => pathnameFor(b.path).length - pathnameFor(a.path).length)[0]
      || legacyRouteNames[location.pathname]
      || { name: 'ERP Dashboard', icon: Gauge };
  }, [location.pathname, location.search]);

  useEffect(() => {
    setMobileOpen(false);
    visibleNavigation.forEach((group) => group.items.forEach((item) => {
      if (item.children?.some((child) => isActive(child.path))) expandMenu(item.name);
    }));
  }, [location.pathname, location.search, visibleNavigation]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const closeOnEscape = (event) => event.key === 'Escape' && setMobileOpen(false);
    document.addEventListener('keydown', closeOnEscape);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', closeOnEscape); document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const displayName = user?.full_name || user?.username || user?.mobile_number || 'ERP User';
  const initials = displayName.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase();

  const Sidebar = ({ mobile = false }) => (
    <aside className={`erp-sidebar ${mobile ? 'erp-sidebar-mobile' : 'erp-sidebar-desktop'} ${sidebarOpen || mobile ? 'erp-sidebar-expanded' : 'erp-sidebar-collapsed'}`} data-testid="sidebar">
      <div className="erp-sidebar-brand">
        <Link to="/" className="erp-brand-mark" aria-label="ZOBOX dashboard"><img src="/assets/zoboxPhoneIcon.png" alt="" /></Link>
        {(sidebarOpen || mobile) && <div className="min-w-0"><div className="erp-brand-name">ZOBOX</div><div className="erp-brand-subtitle">Enterprise Suite</div></div>}
        {mobile && <button onClick={() => setMobileOpen(false)} className="erp-icon-button ml-auto" aria-label="Close navigation"><X size={20} /></button>}
      </div>

      <nav className="erp-sidebar-nav" aria-label="Primary navigation">
        {visibleNavigation.map((group) => <div key={group.label} className="erp-nav-group">
          {(sidebarOpen || mobile) && <div className="erp-nav-label">{group.label}</div>}
          {group.items.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const expanded = expandedMenus[item.name];
            return <div key={item.name} className="erp-nav-item-wrap">
              {item.children ? <button type="button" onClick={() => toggleMenu(item.name)} className={`erp-nav-item ${active ? 'is-active' : ''}`} aria-expanded={Boolean(expanded)} title={!sidebarOpen && !mobile ? item.name : undefined}>
                <Icon size={19} strokeWidth={1.9} /><span className="erp-nav-text">{item.name}</span>{(sidebarOpen || mobile) && <ChevronDown size={15} className={`erp-nav-chevron ${expanded ? 'is-open' : ''}`} />}
              </button> : <Link to={item.path} className={`erp-nav-item ${active ? 'is-active' : ''}`} title={!sidebarOpen && !mobile ? item.name : undefined}>
                <Icon size={19} strokeWidth={1.9} /><span className="erp-nav-text">{item.name}</span>
              </Link>}
              {item.children && expanded && (sidebarOpen || mobile) && <div className="erp-subnav">
                {item.children.map((child) => <Link key={child.path} to={child.path} className={`erp-subnav-item ${isActive(child.path) ? 'is-active' : ''}`}>{child.name}</Link>)}
              </div>}
            </div>;
          })}
        </div>)}
      </nav>

      <div className="erp-sidebar-footer">
        {!mobile && <button onClick={toggleSidebar} className="erp-nav-item" aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}>{sidebarOpen ? <PanelLeftClose size={19} /> : <PanelLeftOpen size={19} />}<span className="erp-nav-text">Collapse menu</span></button>}
      </div>
    </aside>
  );

  return <div className="erp-shell">
    <Sidebar />
    {mobileOpen && <div className="erp-mobile-overlay" onClick={() => setMobileOpen(false)} aria-hidden="true" />}
    {mobileOpen && <Sidebar mobile />}

    <div className="erp-workspace">
      <header className="erp-topbar" data-testid="header">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => setMobileOpen(true)} className="erp-icon-button erp-mobile-menu" aria-label="Open navigation"><Menu size={22} /></button>
          <div className="min-w-0">
            <div className="erp-breadcrumb"><Link to="/">ZOBOX ERP</Link><ChevronRight size={13} /><span>{currentPage.name}</span></div>
            <h1 className="erp-page-title">{currentPage.name}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <button className="erp-icon-button erp-notification-button" aria-label="Notifications"><Bell size={19} /><span className="erp-notification-dot" /></button>
          <div className="erp-user-block">
            <div className="erp-avatar">{initials}</div>
            <div className="erp-user-copy"><div className="erp-user-name">{displayName}</div><div className="erp-user-role">{user?.role || 'User'}</div></div>
          </div>
          <button onClick={onLogout} className="erp-logout-button" data-testid="logout-button"><LogOut size={17} /><span>Logout</span></button>
        </div>
      </header>

      <main className="erp-main">
        <div className="erp-content">{children}</div>
        <Footer />
      </main>
    </div>
  </div>;
}

export default Layout;
